import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ModelPicker } from "@/components/AIModelsCard";
import { ThankYouEditor } from "@/components/ThankYouEditor";
import { RealCoverPreview } from "@/components/RealCoverPreview";
import { api, errMsg } from "@/lib/api";

export const PRESETS = [
  { name: "Terracotta & Gold", dark: "#6E3222", accent: "#C9A24A", tint: "#F5E6DC" },
  { name: "Aubergine & Gold", dark: "#3B1F4A", accent: "#C9A227", tint: "#EDE3F2" },
  { name: "Midnight & Coral", dark: "#1F2A44", accent: "#E07A5F", tint: "#E4E8F2" },
  { name: "Sage & Brass", dark: "#34483A", accent: "#B8954A", tint: "#E5ECE4" },
  { name: "Sepia & Brass", dark: "#4A3426", accent: "#B08D57", tint: "#F0E7DA" },
  { name: "Ink & Silver", dark: "#22303C", accent: "#A8B2BD", tint: "#E3E8EC" },
  { name: "Plum & Gold", dark: "#38171E", accent: "#D4AF37", tint: "#F3E6E8" },
  { name: "Forest & Copper", dark: "#1E3B33", accent: "#C07A4A", tint: "#E2EDE8" },
  { name: "Navy & Cream", dark: "#1B2B4B", accent: "#E8D9B0", tint: "#E5E9F2" },
  { name: "Teal & Saffron", dark: "#14474A", accent: "#E3A83B", tint: "#E0EEEC" },
  { name: "Rose & Charcoal", dark: "#3A2E33", accent: "#D98C9A", tint: "#F6E7EA" },
  { name: "Olive & Ochre", dark: "#3E4426", accent: "#D1A13A", tint: "#EEEEDD" },
];
const HEX = /^#[0-9A-Fa-f]{6}$/;
const SWATCHES = [["dark", "Deep colour (covers, headers)"], ["accent", "Accent (frames, titles)"], ["tint", "Light tint (pages, panels)"]];

const Preview = ({ t, name }) => (
  <div className="flex gap-3" data-testid="category-theme-preview">
    <div className="w-32 aspect-[3/4] p-2 shrink-0" style={{ background: t.dark }}>
      <div className="h-full border flex flex-col items-center justify-center text-center px-2" style={{ borderColor: t.accent }}>
        <div className="text-[7px] tracking-[0.25em] uppercase" style={{ color: t.accent }}>Soft Spine Press</div>
        <div className="font-playfair text-[13px] leading-tight mt-2 text-[#F3EBD6]">{name}</div>
        <div className="w-6 h-px mt-2" style={{ background: t.accent }} />
      </div>
    </div>
    <div className="flex-1 p-3 text-xs" style={{ background: t.tint }}>
      <div className="font-playfair text-base" style={{ color: t.dark }}>Chapter I</div>
      <div className="h-px my-2" style={{ background: t.accent }} />
      <p className="text-[#2A2325]/75 leading-relaxed">Pages, callouts, Instagram slides and delivery emails use these colours.</p>
    </div>
  </div>
);

const Colours = ({ t, setT, catName, cat }) => (
  <div className="space-y-5">
    <Preview t={t} name={catName} />
    <RealCoverPreview cat={cat} theme={t} />
    <div>
      <div className="text-xs text-[#5C5255] mb-2">Start from a palette</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {PRESETS.map((x) => (
          <button key={x.name} type="button" onClick={() => setT({ ...x })} className={`border p-1.5 text-left hover:border-[#2A2325]/50 transition-colors ${t.name === x.name ? "border-[#2A2325]" : "border-[#2A2325]/10"}`} data-testid={`theme-preset-${x.name.replace(/\W+/g, "-").toLowerCase()}`}>
            <div className="flex h-6"><span className="flex-[2]" style={{ background: x.dark }} /><span className="flex-1" style={{ background: x.accent }} /><span className="flex-1" style={{ background: x.tint }} /></div>
            <div className="text-[10px] mt-1 truncate">{x.name}</div>
          </button>
        ))}
      </div>
    </div>
    <div className="grid sm:grid-cols-3 gap-3">
      {SWATCHES.map(([k, label]) => (
        <label key={k} className="text-[11px] text-[#5C5255] space-y-1">
          <span>{label}</span>
          <div className="flex gap-1.5">
            <input type="color" value={HEX.test(t[k]) ? t[k] : "#000000"} onChange={(e) => setT({ ...t, [k]: e.target.value.toUpperCase(), ...(PRESETS.some((x) => x.name === t.name) ? { name: "Custom" } : {}) })} className="w-10 h-9 border border-[#2A2325]/15 bg-white cursor-pointer shrink-0" data-testid={`category-color-${k}`} />
            <Input value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value.trim(), ...(PRESETS.some((x) => x.name === t.name) ? { name: "Custom" } : {}) })} className={`h-9 rounded-none bg-white font-mono-meta text-xs ${HEX.test(t[k]) ? "" : "border-[#9B2C2C]"}`} data-testid={`category-hex-${k}`} />
          </div>
        </label>
      ))}
    </div>
    <label className="block text-xs text-[#5C5255]">Palette name
      <Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} className="mt-1 h-9 rounded-none bg-white" data-testid="category-theme-name" />
    </label>
  </div>
);

export const CategorySettingsDialog = ({ cat, open, onOpenChange, onSaved, tab = "colours" }) => {
  const [t, setT] = useState(null);
  const [name, setName] = useState("");
  const [wm, setWm] = useState("");
  const [useDefault, setUseDefault] = useState(true);
  const [models, setModels] = useState([]);
  const [def, setDef] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !cat) return;
    setT({ name: "", dark: "#38171E", accent: "#D4AF37", tint: "#F3E6E8", ...(cat.theme || {}) });
    setName(cat.name);
    setWm(cat.writing_model || "");
    setUseDefault(!cat.writing_model);
    api.get("/ai-settings").then((r) => setDef(r.data.models.writing)).catch(() => {});
    api.get("/ai-settings/models").then((r) => setModels(r.data.text)).catch(() => {});
  }, [open, cat]);

  if (!cat || !t) return null;
  const valid = SWATCHES.every(([k]) => HEX.test(t[k])) && name.trim();

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/categories/${cat.id}`, { name: name.trim(), theme: { ...t, name: t.name || "Custom" }, writing_model: useDefault ? "" : wm || def });
      toast.success("Category saved — press “Rebuild all files” to recolour existing PDFs");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFDF9] rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="category-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-playfair text-3xl font-normal">Category settings</DialogTitle>
        </DialogHeader>
        <label className="block text-xs text-[#5C5255]">Category name
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-9 rounded-none bg-white text-base" data-testid="category-name-input" />
        </label>
        <Tabs defaultValue={tab}>
          <TabsList className="rounded-none">
            <TabsTrigger value="colours" className="rounded-none" data-testid="category-tab-colours">Colours</TabsTrigger>
            <TabsTrigger value="model" className="rounded-none" data-testid="category-tab-model">Writing model</TabsTrigger>
            <TabsTrigger value="note" className="rounded-none" data-testid="category-tab-note">Thank-you note</TabsTrigger>
          </TabsList>
          <TabsContent value="colours" className="pt-4"><Colours t={t} setT={setT} catName={name} cat={cat} /></TabsContent>
          <TabsContent value="model" className="pt-4 space-y-4">
            <p className="text-sm text-[#5C5255]">The AI model that writes, converts and rewrites titles in this category, including new editions, listings and captions. Fact checks and drawings use the models in Settings.</p>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={useDefault} onCheckedChange={setUseDefault} data-testid="category-model-default-switch" />
              Use the default writing model <span className="font-mono-meta text-xs text-[#5C5255]">({def || "…"})</span>
            </label>
            {!useDefault && <ModelPicker list={models} value={wm || def} onChange={setWm} kind="text" id="category" />}
          </TabsContent>
          <TabsContent value="note" className="pt-4">
            <ThankYouEditor cat={cat} onSaved={onSaved} />
            <p className="text-xs text-[#5C5255] mt-2">The note saves on its own button; colours and the model save below.</p>
          </TabsContent>
        </Tabs>
        <Button onClick={save} disabled={busy || !valid} className="rounded-none gap-2 w-full" style={{ background: HEX.test(t.dark) ? t.dark : undefined }} data-testid="category-settings-save-btn">
          {busy && <Loader2 size={14} className="animate-spin" />} Save colours, name & model
        </Button>
      </DialogContent>
    </Dialog>
  );
};
